import useMyCircles from '@/member/hooks/useMyCircles'
import useMyRoles from '@/member/hooks/useMyRoles'
import { useMemo, useState } from 'react'
import {
  CirclePerimeter,
  initialPerimeter,
  offeredPerimeters,
} from '../circlePerimeter'
import { SearchItem } from '../searchTypes'

export interface UseCirclePerimeterResult {
  offered: CirclePerimeter[]
  perimeter: CirclePerimeter
  setPerimeter(perimeter: CirclePerimeter): void
  isInPerimeter(item: SearchItem): boolean
  isPartitioned: boolean
}

// ORG-02: local perimeter state for one circle/role picker instance, plus the
// membership predicate used by useSearch's soft partition.
// `meetingCircleIds` is given when the picker was opened from a meeting (#123):
// the meeting's circle, its roles and sub-circles. It switches the offered
// filters to the meeting's.
export default function useCirclePerimeter(
  defaultPerimeter: CirclePerimeter,
  meetingCircleIds?: string[]
): UseCirclePerimeterResult {
  const offered = offeredPerimeters(!!meetingCircleIds)
  const [chosen, setPerimeter] = useState<CirclePerimeter>(() =>
    initialPerimeter(offered, defaultPerimeter)
  )
  // A choice made before the meeting became known (or after it ended) may no
  // longer be offered: fall back on the default the offered filters allow.
  const perimeter = offered.includes(chosen)
    ? chosen
    : initialPerimeter(offered, defaultPerimeter)
  const myRoles = useMyRoles()
  const myCircles = useMyCircles()

  const myRolesIds = useMemo(
    () => new Set(myRoles.map((circle) => circle.id)),
    [myRoles]
  )
  const myCirclesIds = useMemo(
    () => new Set(myCircles.map((circle) => circle.id)),
    [myCircles]
  )
  const meetingIds = useMemo(
    () => new Set(meetingCircleIds ?? []),
    [meetingCircleIds]
  )

  const isInPerimeter = useMemo(() => {
    if (perimeter === 'meetingCircle') {
      return (item: SearchItem) => meetingIds.has(item.id)
    }
    if (perimeter === 'myRoles') {
      return (item: SearchItem) => myRolesIds.has(item.id)
    }
    if (perimeter === 'myCircles') {
      return (item: SearchItem) => myCirclesIds.has(item.id)
    }
    return () => true
  }, [perimeter, meetingIds, myRolesIds, myCirclesIds])

  return {
    offered,
    perimeter,
    setPerimeter,
    isInPerimeter,
    isPartitioned: perimeter !== 'all',
  }
}
